import React, { useState, useEffect } from 'react';

interface TutorialStage {
  id: string;
  title: string;
  content: React.ReactNode;
  highlight?: string;
  requiresKeyPress?: string;
  keyPressDescription?: string;
}

interface TutorialProps {
  onComplete: () => void;
  onSkip: () => void;
}

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 8px',
  background: '#21262d',
  border: '1px solid #30363d',
  color: '#e2e8f0',
  fontSize: '11px',
  fontFamily: 'var(--font-mono)',
  minWidth: '24px',
  textAlign: 'center',
};

const TUTORIAL_STAGES: TutorialStage[] = [
  {
    id: 'welcome',
    title: 'Welcome to Grand Online',
    content: (
      <div>
        <p>Welcome, Captain! This quick tour covers everything you need to start sailing.</p>
        <p style={{ marginTop: '12px', color: '#8b949e', fontSize: '11px' }}>
          You can skip at any time using the button below, or press ESC.
        </p>
      </div>
    ),
  },
  {
    id: 'movement',
    title: 'Navigation Controls',
    content: (
      <div>
        <p>Click-to-move is the core of navigation and combat targeting:</p>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>LEFT CLICK</kbd>
            <span>Click on water to sail to that location</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>LEFT CLICK</kbd>
            <span>Click on enemy ships to chase and engage</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>SPACE</kbd>
            <span>Stop movement / Toggle fishing minigame</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>ESC</kbd>
            <span>Disengage current target</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'ui_panels',
    title: 'Quick Access Panels',
    content: (
      <div>
        <p>Memorize these core hotkeys so you can react fast at sea:</p>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>I</kbd>
            <span>Inventory - Items, equipment, consumables</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>M</kbd>
            <span>World Map - Plan routes and set destinations</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>C</kbd>
            <span>Crafting - Build gear and upgrades</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>ESC</kbd>
            <span>Close any open panel or menu</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'hud',
    title: 'HUD Essentials',
    content: (
      <div>
        <p>Focus on these four signals while you sail:</p>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#ef4444' }}>Hull Health</strong> - If it reaches 0, you sink and respawn at a safe harbor.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#f0f0f0' }}>Sail Health</strong> - Damaged sails reduce your speed and turning.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#fbbf24' }}>Pieces [P]</strong> - Earn by fishing and combat, spend on repairs and upgrades.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#a855f7' }}>Infamy</strong> - Higher infamy increases danger and rewards.
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'progression',
    title: 'Your First Goals',
    content: (
      <div>
        <p>Start with safe wins, then scale up:</p>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#22c55e' }}>Upgrade Your Ship</strong> - Visit a Shipyard to move beyond the starter vessel.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#3b82f6' }}>Fish for Pieces</strong> - Reliable income with low risk.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#f59e0b' }}>Pick Fights Carefully</strong> - Stronger enemies drop better rewards.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong style={{ color: '#a855f7' }}>Grow Your Skills</strong> - Sailing, combat, and fishing all improve with use.
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'combat',
    title: 'Combat Basics',
    content: (
      <div>
        <p>Combat is automatic once you are in range:</p>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong>Engage</strong> - Click an enemy to pursue and auto-fire.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong>Retreat</strong> - Press ESC to disengage, then click away to flee.
          </div>
          <div style={{ padding: '8px', background: '#161b22', border: '1px solid #21262d' }}>
            <strong>Loot</strong> - Defeating enemies drops pieces and sometimes items.
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'complete',
    title: 'Ready to Sail!',
    content: (
      <div>
        <p>Key bindings to remember:</p>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>I</kbd>
            <span>Inventory</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>M</kbd>
            <span>World Map</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>C</kbd>
            <span>Crafting</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>SPACE</kbd>
            <span>Stop / Fish</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <kbd style={kbdStyle}>ESC</kbd>
            <span>Disengage / Close</span>
          </div>
        </div>
        <p style={{ marginTop: '16px', color: '#22c55e' }}>Good luck, Captain. The open water is yours.</p>
      </div>
    ),
  },
];

export const Tutorial: React.FC<TutorialProps> = ({ onComplete, onSkip }): React.ReactElement => {
  const [currentStage, setCurrentStage] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const stage = TUTORIAL_STAGES[currentStage];
  const isFirstStage = currentStage === 0;
  const isLastStage = currentStage === TUTORIAL_STAGES.length - 1;

  const handleNext = (): void => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStage((prev) => Math.min(prev + 1, TUTORIAL_STAGES.length - 1));
      setIsAnimating(false);
    }, 150);
  };

  const handlePrevious = (): void => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStage((prev) => Math.max(prev - 1, 0));
      setIsAnimating(false);
    }, 150);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLastStage) {
          onComplete();
        } else {
          handleNext();
        }
      } else if (e.key === 'ArrowLeft' && !isFirstStage) {
        handlePrevious();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStage, isLastStage, isFirstStage, onComplete, onSkip]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        fontFamily: 'var(--font-mono)',
      }}
    >
      <div
        style={{
          background: '#0d1117',
          border: '2px solid #21262d',
          width: '100%',
          maxWidth: '550px',
          padding: '24px',
          opacity: isAnimating ? 0.5 : 1,
          transform: isAnimating ? 'scale(0.98)' : 'scale(1)',
          transition: 'opacity 0.15s, transform 0.15s',
        }}
      >
        {/* Progress bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '10px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Tutorial
            </span>
            <span style={{ fontSize: '10px', color: '#8b949e' }}>
              {currentStage + 1} / {TUTORIAL_STAGES.length}
            </span>
          </div>
          <div style={{ height: '4px', background: '#21262d', width: '100%' }}>
            <div
              style={{
                height: '100%',
                background: '#58a6ff',
                width: `${((currentStage + 1) / TUTORIAL_STAGES.length) * 100}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>

        {/* Stage indicator dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '20px' }}>
          {TUTORIAL_STAGES.map((_, index) => (
            <div
              key={index}
              style={{
                width: '8px',
                height: '8px',
                background: index === currentStage ? '#58a6ff' : index < currentStage ? '#238636' : '#21262d',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onClick={() => !isAnimating && setCurrentStage(index)}
            />
          ))}
        </div>

        {/* Title */}
        <h2 style={{ margin: '0 0 16px 0', color: '#e2e8f0', fontSize: '18px', fontFamily: 'var(--font-mono)' }}>
          {stage.title}
        </h2>

        {/* Content */}
        <div style={{ color: '#c9d1d9', fontSize: '13px', lineHeight: '1.6', minHeight: '200px' }}>
          {stage.content}
        </div>

        {/* Navigation buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '24px' }}>
          <button
            onClick={onSkip}
            style={{
              padding: '10px 16px',
              background: '#3d1a1a',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 600,
              flex: 1,
            }}
          >
            SKIP TUTORIAL
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            {!isFirstStage && (
              <button
                onClick={handlePrevious}
                disabled={isAnimating}
                style={{
                  padding: '8px 16px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  color: '#e2e8f0',
                  cursor: isAnimating ? 'wait' : 'pointer',
                  fontSize: '11px',
                  opacity: isAnimating ? 0.5 : 1,
                }}
              >
                BACK
              </button>
            )}
            <button
              onClick={isLastStage ? onComplete : handleNext}
              disabled={isAnimating}
              style={{
                padding: '8px 20px',
                background: isLastStage ? '#238636' : '#21262d',
                border: `1px solid ${isLastStage ? '#2ea043' : '#30363d'}`,
                color: '#fff',
                cursor: isAnimating ? 'wait' : 'pointer',
                fontSize: '11px',
                fontWeight: 500,
                opacity: isAnimating ? 0.5 : 1,
              }}
            >
              {isLastStage ? 'START PLAYING' : 'NEXT'}
            </button>
          </div>
        </div>

        {/* Keyboard hint */}
        <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '10px', color: '#6b7280' }}>
          Use Arrow Keys or Enter to navigate • ESC to skip
        </div>
      </div>
    </div>
  );
};
